import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

const OPTIONS = [
  {
    id: "private",
    labelKey: "pages.presentationImages.privateLabel",
    shortKey: "pages.presentationImages.privateShort",
    descKey: "pages.presentationImages.privateDesc",
    summaryKey: "pages.presentationImages.privateSummary",
  },
  {
    id: "public",
    labelKey: "pages.presentationImages.publicLabel",
    shortKey: "pages.presentationImages.publicShort",
    descKey: "pages.presentationImages.publicDesc",
    summaryKey: "pages.presentationImages.publicSummary",
  },
  {
    id: "both",
    labelKey: "pages.presentationImages.bothLabel",
    shortKey: "pages.presentationImages.bothShort",
    descKey: "pages.presentationImages.bothDesc",
    summaryKey: "pages.presentationImages.bothSummary",
  },
];

export default function ImageSourcePicker({
  value,
  onChange,
  compact = false,
  layout = "cards",
  preferOwn = true,
  onPreferOwnChange,
}) {
  const { t } = useTranslation();
  const selected = OPTIONS.find((option) => option.id === value) || OPTIONS[2];

  const select = (id) => onChange(id);

  if (layout === "segmented") {
    const summary =
      selected.id === "both" && !preferOwn
        ? t("pages.presentationImages.bothEqualSummary")
        : t(selected.summaryKey);
    return (
      <Box>
        <Typography sx={{ mb: 0.7, fontSize: 12, fontWeight: 700, color: "#334155", letterSpacing: "-0.01em" }}>
          {t("pages.presentationImages.ask")}
        </Typography>
        <Box
          role="radiogroup"
          aria-label={t("pages.presentationImages.title")}
          sx={{
            display: "flex",
            p: "3px",
            borderRadius: 999,
            bgcolor: "#f1f5f9",
            border: "1px solid rgba(226,232,240,1)",
          }}
        >
          {OPTIONS.map((option) => {
            const active = value === option.id;
            return (
              <Box
                key={option.id}
                role="radio"
                aria-checked={active}
                tabIndex={0}
                onClick={() => select(option.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    select(option.id);
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: 0.6,
                  py: 0.55,
                  borderRadius: 999,
                  cursor: "pointer",
                  textAlign: "center",
                  bgcolor: active ? "#fff" : "transparent",
                  boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
                  color: active ? "#0369a1" : "#64748b",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  userSelect: "none",
                }}
              >
                {t(option.shortKey)}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: 0.7, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 11.5, lineHeight: 1.35, color: "#64748b" }}>
            {summary}
          </Typography>
          {value === "both" && onPreferOwnChange ? (
            <Box
              role="checkbox"
              aria-checked={preferOwn}
              tabIndex={0}
              onClick={() => onPreferOwnChange(!preferOwn)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onPreferOwnChange(!preferOwn);
                }
              }}
              sx={{
                flexShrink: 0,
                px: 0.9,
                py: 0.25,
                borderRadius: 999,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1.4,
                color: preferOwn ? "#0369a1" : "#64748b",
                bgcolor: preferOwn ? "rgba(14,165,233,0.12)" : "transparent",
                border: "1px solid",
                borderColor: preferOwn ? "rgba(14,165,233,0.35)" : "rgba(226,232,240,1)",
              }}
            >
              {t("pages.presentationImages.preferOwnShort")}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {!compact ? (
        <Typography sx={{ mb: 1.25, color: "#64748b", fontSize: 13, lineHeight: 1.45 }}>
          {t("pages.presentationImages.subtitle")}
        </Typography>
      ) : null}
      <Box
        role="radiogroup"
        aria-label={t("pages.presentationImages.title")}
        sx={{
          display: "flex",
          flexDirection: compact ? "row" : "column",
          gap: compact ? 0.75 : 0.75,
        }}
      >
        {OPTIONS.map((option) => {
          const active = value === option.id;
          return (
            <Box
              key={option.id}
              role="radio"
              aria-checked={active}
              tabIndex={0}
              onClick={() => onChange(option.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onChange(option.id);
                }
              }}
              sx={{
                flex: compact ? 1 : "none",
                minWidth: 0,
                px: compact ? 1 : 1.4,
                py: compact ? 0.9 : 1.2,
                borderRadius: 2.5,
                cursor: "pointer",
                border: "1px solid",
                borderColor: active ? "rgba(14,165,233,0.45)" : "rgba(226,232,240,1)",
                bgcolor: active ? "rgba(14,165,233,0.08)" : "#fff",
                transition: "border-color 160ms ease, background-color 160ms ease",
                "&:hover": {
                  borderColor: active ? "rgba(14,165,233,0.6)" : "rgba(148,163,184,0.7)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: compact ? 12 : 13.5,
                  fontWeight: 700,
                  color: "#0f172a",
                  lineHeight: 1.3,
                }}
              >
                {t(compact ? option.shortKey : option.labelKey)}
              </Typography>
              {!compact ? (
                <Typography sx={{ mt: 0.35, fontSize: 12.5, lineHeight: 1.4, color: "#64748b" }}>
                  {t(option.descKey)}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>
      {compact ? (
        <Typography sx={{ mt: 0.8, fontSize: 12, lineHeight: 1.4, color: "#64748b" }}>
          {t(selected.descKey)}
        </Typography>
      ) : null}
      {value === "both" && onPreferOwnChange ? (
        <Box
          role="checkbox"
          aria-checked={preferOwn}
          tabIndex={0}
          onClick={() => onPreferOwnChange(!preferOwn)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onPreferOwnChange(!preferOwn);
            }
          }}
          sx={{
            mt: 1,
            px: 1.2,
            py: 0.9,
            borderRadius: 2,
            cursor: "pointer",
            border: "1px solid rgba(226,232,240,1)",
            bgcolor: preferOwn ? "rgba(14,165,233,0.06)" : "#fff",
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
            {t("pages.presentationImages.preferOwn")}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
